import { useGetIdentity, useGetMany } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import type { Contact } from "../types";
import {
  useResolveApprovalItem,
  type ApprovalQueueItem,
} from "./useApprovalQueue";

const QUEUE_TYPE_LABEL: Record<string, string> = {
  martha_outreach_draft: "Martha — drafted outreach",
  marco_list4_candidate: "Marco — Fractional CTO candidate",
};

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const ProposalPreview = ({ item }: { item: ApprovalQueueItem }) => {
  const proposal = item.resolution_payload?.proposal as
    | Record<string, unknown>
    | undefined;
  if (!proposal) return null;

  if (item.queue_type === "martha_outreach_draft") {
    return (
      <div className="mt-2 rounded-md border bg-muted/40 p-3 text-sm">
        {typeof proposal.subject === "string" && (
          <p className="font-medium">{proposal.subject}</p>
        )}
        {typeof proposal.body === "string" && (
          <p className="text-muted-foreground mt-1 whitespace-pre-wrap">
            {proposal.body}
          </p>
        )}
        <div className="mt-2 flex flex-wrap gap-1">
          {typeof proposal.sending_identity === "string" && (
            <Badge variant="outline">{proposal.sending_identity}</Badge>
          )}
          {typeof proposal.channel === "string" && (
            <Badge variant="outline">{proposal.channel}</Badge>
          )}
        </div>
      </div>
    );
  }

  if (item.queue_type === "marco_list4_candidate") {
    return (
      <div className="mt-2 rounded-md border bg-muted/40 p-3 text-sm">
        {typeof proposal.attorney_count === "number" && (
          <p>Attorney count: {proposal.attorney_count}</p>
        )}
        {typeof proposal.job_posting_excerpt === "string" && (
          <p className="text-muted-foreground mt-1">
            {proposal.job_posting_excerpt}
          </p>
        )}
      </div>
    );
  }

  return null;
};

export const ApprovalQueueCard = ({ item }: { item: ApprovalQueueItem }) => {
  const { identity } = useGetIdentity();
  const { mutate: resolve, isPending } = useResolveApprovalItem();
  const { data: contacts } = useGetMany<Contact>(
    "contacts",
    { ids: item.contact_id ? [item.contact_id] : [] },
    { enabled: !!item.contact_id },
  );
  const contact = contacts?.[0];

  const handleResolve = (action: "approved" | "rejected") => {
    resolve({
      item,
      action,
      resolvedBy: identity?.fullName ?? identity?.id?.toString() ?? "unknown",
    });
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-2 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">
            {QUEUE_TYPE_LABEL[item.queue_type] ?? item.queue_type}
          </Badge>
          {item.priority === "high" && (
            <Badge variant="destructive">High priority</Badge>
          )}
          <span className="text-muted-foreground ml-auto text-xs">
            {fmtDateTime(item.created_at)}
          </span>
        </div>
        <p className="text-sm font-medium">{item.reason}</p>
        {contact && (
          <p className="text-muted-foreground text-xs">
            {contact.first_name} {contact.last_name}
          </p>
        )}
        <ProposalPreview item={item} />
        <div className="mt-2 flex gap-2">
          <Button
            size="sm"
            onClick={() => handleResolve("approved")}
            disabled={isPending}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleResolve("rejected")}
            disabled={isPending}
          >
            Reject
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
